import { createContext, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../app/stores/store";
import { bringToFront, closeWindow, minimizeWindow, openWindow } from "../../../app/stores/window-slice";
import { Window } from "../types";

interface WindowContextType {
    windows: Window[];
    closeWindow: (id: string) => void;
    minimizeWindow: (id: string) => void;
    bringToFront: (id: string) => void;
    openWindow: (id: string, title: string, component: React.ReactNode) => void;
}

const WindowContext = createContext<WindowContextType | null>(null);

export const WindowProvider = ({ children }: { children: React.ReactNode }) => {
    const dispatch = useDispatch();
    const { windows } = useSelector((state: RootState) => state.window);

    const handleCloseWindow = (id: string) => {
        dispatch(closeWindow(id));
    };

    const handleMinimizeWindow = (id: string) => {
        dispatch(minimizeWindow(id));
    };

    const handleBringToFront = (id: string) => {
        dispatch(bringToFront(id));
    };

    const handleOpenWindow = (id: string, title: string, component: React.ReactNode) => {
        dispatch(openWindow({ id, title, component }));
    };

    return (
        <WindowContext.Provider
            value={{
                windows,
                closeWindow: handleCloseWindow,
                minimizeWindow: handleMinimizeWindow,
                bringToFront: handleBringToFront,
                openWindow: handleOpenWindow,
            }}>
            {children}
        </WindowContext.Provider>
    );
};

export const useWindow = () => {
    const context = useContext(WindowContext);
    if (!context) {
        throw new Error("useWindow must be used within a WindowProvider");
    }
    return context;
};
